import React, { useState, useEffect } from 'react';
import { api } from '../api';

const PERIODS = [['3m', '3 months'], ['6m', '6 months'], ['12m', '12 months'], ['ytd', 'Year to date']];
const fmtQty = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
const fmtMoney = (n, cur) => (cur === 'USD' ? 'US$' : 'A$') + Math.round(n || 0).toLocaleString();
const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, '-');

function Kpi({ testid, num, lbl }) {
  return <div className="si-fc-kpi" data-testid={testid}><span className="si-fc-num">{num}</span><span className="si-fc-lbl">{lbl}</span></div>;
}

export function useItemSearch(q, period) {
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    setRes([]); setErr('');
    if (q.trim().length < 2) { setBusy(false); return; }
    setBusy(true);
    let live = true;
    const t = setTimeout(() => api.salesIntelItemSearch(q.trim(), period)
      .then(d => live && setRes(d.items || []))
      .catch(e => live && setErr(e.response?.data?.detail || e.message))
      .finally(() => live && setBusy(false)), 350);
    return () => { live = false; clearTimeout(t); };
  }, [q, period]);
  return { res, busy, err };
}

export function SearchResult({ r, checked, onToggle, onOpen, testid }) {
  return (
    <div className={'si-sr-row' + (r.inactive ? ' inactive' : '')} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onToggle} data-testid={testid + '-pick'} aria-label={'Select ' + r.item} />
      <button className="si-sr-main" onClick={onOpen} data-testid={testid + '-open'} title="Open breakdown">
        <b>{r.item}</b>{r.name && r.name !== r.item && <span className="si-sr-name">{r.name}</span>}
        {r.inactive && <span className="si-sr-pill" data-testid={testid + '-inactive'}>inactive</span>}
        {r.from_generic && <span className="si-gen-pill">from generic</span>}
      </button>
      <span className="si-sr-qty">Q {fmtQty(r.quoted_qty)} · O {fmtQty(r.ordered_qty)}</span>
    </div>
  );
}

function AddItems({ items, period, onAdd }) {
  const [q, setQ] = useState('');
  const { res, busy, err } = useItemSearch(q, period);
  const fresh = res.filter(r => !items.includes(r.item));
  return (
    <div className="si-bd-add">
      <input className="input" data-testid="si-bd-search" placeholder="Add more items to combine — search NetSuite by code or name…" value={q} onChange={e => setQ(e.target.value)} />
      {busy && <div className="si-fc-note" style={{ marginLeft: 0 }}>Searching NetSuite…</div>}
      {err && <div className="si-error">{err}</div>}
      {!busy && fresh.length > 0 && (
        <div className="si-bd-results" data-testid="si-bd-results">
          <button className="btn btn-sm" data-testid="si-bd-add-all" onClick={() => { onAdd(fresh.map(r => r.item)); setQ(''); }}>+ Add all {fresh.length}</button>
          {fresh.map(r => (
            <button key={r.item} className={'si-bd-result' + (r.inactive ? ' inactive' : '')} data-testid={'si-bd-add-' + slug(r.item)} onClick={() => onAdd([r.item])}>
              + {r.item} <span>{r.inactive ? 'inactive · ' : ''}Q {fmtQty(r.quoted_qty)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PeriodTable({ d, cur }) {
  return (
    <div className="si-bd-scroll"><table className="si-table si-items-table" data-testid="si-bd-periods">
      <thead><tr><th>{d.bucket === 'week' ? 'Week of' : 'Month'}</th><th className="num">Quotes</th><th className="num">Quoted qty</th><th className="num">Quoted $</th>
        <th className="num">Orders</th><th className="num">Ordered qty</th><th className="num">Ordered $</th><th className="num">PO qty</th></tr></thead>
      <tbody>
        {d.buckets.map(b => (
          <tr key={b.start} data-testid={'si-bd-period-' + b.start}>
            <td>{b.label}</td><td className="num">{b.quote_count || '—'}</td><td className="num">{fmtQty(b.quoted_qty)}</td><td className="num">{fmtMoney(b.quoted_amount, cur)}</td>
            <td className="num">{b.order_count || '—'}</td><td className="num">{fmtQty(b.ordered_qty)}</td><td className="num">{fmtMoney(b.ordered_amount, cur)}</td><td className="num">{b.po_qty ? fmtQty(b.po_qty) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function PerItemTable({ d, cur, onRemove }) {
  return (
    <div className="si-bd-scroll"><table className="si-table si-items-table" data-testid="si-bd-per-item">
      <thead><tr><th>Item</th><th className="num">Quoted qty</th><th className="num">Quoted $</th><th className="num">Ordered qty</th><th className="num">Ordered $</th><th className="num">PO qty</th><th className="num">PO open</th><th className="num">On hand</th><th /></tr></thead>
      <tbody>
        {d.items.map(it => (
          <tr key={it.item} data-testid={'si-bd-item-' + slug(it.item)}>
            <td className="si-bd-code" title={it.name}>{it.item}</td>
            <td className="num">{fmtQty(it.quoted_qty)}</td><td className="num">{fmtMoney(it.quoted_amount, cur)}</td>
            <td className="num">{fmtQty(it.ordered_qty)}</td><td className="num">{fmtMoney(it.ordered_amount, cur)}</td>
            <td className="num">{fmtQty(it.po_qty)}</td><td className="num">{fmtQty(it.po_open)}</td><td className="num">{fmtQty(it.on_hand)}</td>
            <td><button className="btn btn-sm btn-ghost" data-testid={'si-bd-remove-' + slug(it.item)} onClick={() => onRemove(it.item)} title="Remove from this group">×</button></td>
          </tr>
        ))}
        <tr className="si-bd-total" data-testid="si-bd-per-item-total">
          <td><b>Combined</b></td><td className="num">{fmtQty(d.totals.quoted_qty)}</td><td className="num">{fmtMoney(d.totals.quoted_amount, cur)}</td>
          <td className="num">{fmtQty(d.totals.ordered_qty)}</td><td className="num">{fmtMoney(d.totals.ordered_amount, cur)}</td>
          <td className="num">{fmtQty(d.totals.po_qty)}</td><td className="num">{fmtQty(d.totals.po_open)}</td><td className="num">{fmtQty(d.totals.on_hand)}</td><td />
        </tr>
      </tbody>
    </table></div>
  );
}

function TxTable({ rows, total, kind, cur, multi }) {
  if (!rows.length) return <div className="empty-note" data-testid={'si-bd-' + kind + '-empty'}>None in this period.</div>;
  const po = kind === 'pos';
  return (
    <>
    {total > rows.length && <p className="si-fc-note" data-testid={'si-bd-' + kind + '-capped'} style={{ marginLeft: 0 }}>Showing the latest {rows.length.toLocaleString()} of {total.toLocaleString()} lines — totals above include all of them.</p>}
    <div className="si-bd-scroll"><table className="si-table si-items-table" data-testid={'si-bd-' + kind + '-table'}>
      <thead><tr><th>Date</th><th>Number</th>{multi && <th>Item</th>}<th>{po ? 'Vendor' : 'Client'}</th>{!po && <th>Rep</th>}<th>Status</th>
        <th className="num">Qty</th>{po && <th className="num">Received</th>}{po && <th className="num">Open</th>}<th className="num">{po ? 'Value (PO cur.)' : 'Value'}</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.tranid + '-' + i} data-testid={'si-bd-' + kind + '-row-' + i}>
            <td>{r.date}</td><td>{r.tranid}</td>{multi && <td className="si-bd-code">{r.item}</td>}
            <td>{po ? r.vendor : r.client}</td>{!po && <td>{r.rep}</td>}<td>{r.status}</td>
            <td className="num">{fmtQty(r.qty)}</td>{po && <td className="num">{fmtQty(r.received)}</td>}{po && <td className="num">{fmtQty(r.open_qty)}</td>}
            <td className="num">{po ? Math.round(r.amount).toLocaleString() + ' ' + (r.currency || '') : fmtMoney(r.amount, cur)}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
    </>
  );
}

export default function SiItemsBreakdown({ items: initial, segment, subsidiary, onClose }) {
  const [items, setItems] = useState(initial);
  const [period, setPeriod] = useState('12m');
  const [name, setName] = useState('');
  const [tab, setTab] = useState('periods');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!items.length) return;
    setD(null); setErr(null);
    api.salesIntelItemsBreakdown(items, period, segment, subsidiary).then(setD).catch(e => setErr(e.response?.data?.detail || e.message));
  }, [items, period, segment, subsidiary]);

  const multi = items.length > 1;
  const cur = d?.currency;
  const t = d?.totals || {};
  const add = (ks) => setItems(x => [...x, ...ks.filter(k => !x.includes(k))]);
  const remove = (k) => setItems(x => (x.length > 1 ? x.filter(i => i !== k) : x));
  const tabs = [['periods', 'By period'], ...(multi ? [['items', 'Per item']] : []), ['quotes', `Quotes (${t.quote_count || 0})`], ['orders', `Sales orders (${t.order_count || 0})`], ['pos', `Purchase orders (${t.po_count || 0})`]];

  return (
    <div className="modal-overlay" data-testid="si-bd-modal" onClick={onClose}>
      <div className="modal si-rep-modal" onClick={e => e.stopPropagation()}>
        <div className="si-rep-head">
          <div className="si-rep-title si-bd-head">
            {multi
              ? <input className="input si-bd-name" data-testid="si-bd-name" placeholder={`Combined group — ${items.length} items (name it…)`} value={name} onChange={e => setName(e.target.value)} />
              : <h3 data-testid="si-bd-title">{d?.items?.[0]?.name || items[0]}</h3>}
            <span className="si-rep-window">{multi ? items.length + ' items treated as one · ' : items[0] + ' · '}{d ? `${d.period_start} → ${d.period_end}` : 'loading…'}</span>
          </div>
          <button className="modal-close" data-testid="si-bd-close" onClick={onClose}>×</button>
        </div>
        <div className="si-drill-body">
          <div className="si-periods" data-testid="si-bd-periods-switch">
            {PERIODS.map(([k, l]) => <button key={k} className={'si-period' + (period === k ? ' on' : '')} data-testid={'si-bd-period-btn-' + k} onClick={() => setPeriod(k)}>{l}</button>)}
          </div>
          <AddItems items={items} period={period} onAdd={add} />
          {err && <div className="si-error" data-testid="si-bd-error">{err}</div>}
          {!d && !err && <div className="empty-note">Loading breakdown…</div>}
          {d && (
            <>
              <div className="si-item-stats si-bd-kpis" data-testid="si-bd-kpis">
                <Kpi testid="si-bd-quoted" num={fmtQty(t.quoted_qty)} lbl={`Quoted qty · ${fmtMoney(t.quoted_amount, cur)} on ${t.quote_count} quotes`} />
                <Kpi testid="si-bd-ordered" num={fmtQty(t.ordered_qty)} lbl={`Ordered qty · ${fmtMoney(t.ordered_amount, cur)} on ${t.order_count} sales orders`} />
                <Kpi testid="si-bd-conv" num={t.quoted_qty ? Math.round((t.ordered_qty / t.quoted_qty) * 100) + '%' : '—'} lbl="Ordered ÷ quoted qty" />
                <Kpi testid="si-bd-po" num={fmtQty(t.po_qty)} lbl={`On ${t.po_count} purchase orders · ${fmtQty(t.po_received)} received`} />
                <Kpi testid="si-bd-po-open" num={fmtQty(t.po_open)} lbl="Still to receive on open POs" />
                <Kpi testid="si-bd-onhand" num={fmtQty(t.on_hand)} lbl="On hand now" />
              </div>
              {d.po_error && <div className="si-error" data-testid="si-bd-po-error">{d.po_error}</div>}
              <div className="si-drill-switch si-bd-tabs" role="tablist">
                {tabs.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={'si-drill-tab' + (tab === k ? ' on' : '')} data-testid={'si-bd-tab-' + k} onClick={() => setTab(k)}><b>{l}</b></button>)}
              </div>
              {tab === 'periods' && <PeriodTable d={d} cur={cur} />}
              {tab === 'items' && multi && <PerItemTable d={d} cur={cur} onRemove={remove} />}
              {tab === 'quotes' && <TxTable rows={d.quotes} total={d.quote_rows} kind="quotes" cur={cur} multi={multi} />}
              {tab === 'orders' && <TxTable rows={d.orders} total={d.order_rows} kind="orders" cur={cur} multi={multi} />}
              {tab === 'pos' && <TxTable rows={d.pos} total={d.po_rows} kind="pos" cur={cur} multi={multi} />}
              <p className="si-fc-note" style={{ marginLeft: 0, marginTop: 8 }}>Quotes and sales orders come from the nightly NetSuite sync for the selected period. Purchase orders are read live from NetSuite: every PO raised in the period plus any older PO still open. Grouping is temporary — close this window to reset.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
